import { DataService } from "@/services/data";
import { RouteObject, defer, redirect } from "react-router-dom";
import { PaginatedResponse } from "@/types/common";
import { ProfessionType } from "@/types/content/profession";
import { FundingOrganismType } from "@/types/content/funding_organism";
import { ProfessionalStatusType } from "@/types/content/professional_status";
import { LightProductType } from "@/types/content/product";
import { ProductThemeType } from "@/types/content/product_theme";

const SanteRoute: RouteObject = {
    path: "sante",
    children: [
        {
            index: true,
            async lazy() {
                let { SantePage } = await import("@/pages/content/sante/index");
                return { Component: SantePage };
            },
            loader: async ({ request }) => {
                const professions = (await DataService.client.url(`/content/professions/?sante=1&page_size=1000`).get().json()) as PaginatedResponse<ProfessionType>;
                const funding_organisms = (await DataService.client.url(`/content/funding_organisms?page_size=1000`).get().json()) as PaginatedResponse<FundingOrganismType>;
                const professional_statuses = (await DataService.client
                    .url(`/content/professional_statuses/?display=true&page_size=1000`)
                    .get()
                    .json()) as PaginatedResponse<ProfessionalStatusType>;

                const url = new URL(request.url);
                let product_themes = undefined;
                let professionsParams = url.searchParams.get("professions")

                url.searchParams.set("domain", "sante");
                url.searchParams.set("professions", professionsParams || "");
                if (professionsParams) {
                    url.searchParams.set("page_size", url.searchParams.get("page_size") || "8");
                    url.searchParams.set("is_active", "1");
                    product_themes = (await DataService.client
                        .url(`/content/product_themes/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                        .get()
                        .json()) as PaginatedResponse<ProductThemeType>;
                }

                return defer({
                    professions: professions.results.sort((a, b) => a.name.localeCompare(b.name)),
                    funding_organisms: funding_organisms.results.sort((a, b) => a.name.localeCompare(b.name)),
                    professional_statuses: professional_statuses.results.sort((a, b) => a.name.localeCompare(b.name)),
                    product_themes: product_themes
                });
            }
        },
        {
            id: "detail",
            path: ":profession/:id",
            async lazy() {
                let { SanteDetailPage } = await import("@/pages/content/sante/detail");
                return { Component: SanteDetailPage };
            },
            loader: async ({ params }) => {
                const id = params.id;
                const product_theme = (await DataService.client.url(`/content/product_themes/${id}`).get().json()) as ProductThemeType;
                return { product_theme };
            }
        },
        {
            // Redirect route: resolves a product_code to its theme and redirects to the
            // proper detail page. Used by the funder table when theme_id is unknown
            // (products without a ProductTheme entry).
            path: ":profession/by-product/:product_code",
            loader: async ({ params, request }) => {
                const { profession, product_code } = params;
                const url = new URL(request.url);
                // Preserve query params (funding_organism, etc.) in the target URL.
                const qs = new URLSearchParams();
                for (const [key, value] of url.searchParams.entries()) qs.set(key, value);
                qs.set("product", product_code!);

                try {
                    const data = (await DataService.client
                        .url(`/content/products/?code=${product_code}&page_size=1`)
                        .get()
                        .json()) as PaginatedResponse<LightProductType>;
                    const product = data.results[0];
                    // product_themes is returned as full objects ({id, ...}) by the
                    // products endpoint, even though LightProductType types it as number[].
                    const rawTheme = product?.product_themes?.[0] as unknown;
                    const themeId = typeof rawTheme === "number"
                        ? rawTheme
                        : (rawTheme as { id?: number } | undefined)?.id;
                    if (themeId) {
                        return redirect(`/catalogue/sante/${profession}/${themeId}?${qs.toString()}`);
                    }
                } catch {
                    // fall through to listing
                }
                return redirect(`/catalogue/sante?professions=${profession}`);
            },
            // This route only redirects — no component needed.
            element: null,
        }
    ]
};
export default SanteRoute;
